import { useEffect, useMemo, useRef } from 'react'
import { Button, Empty, Spin } from '@crosscode/gravity-components'
import { useAuth } from '@/app/AuthProvider'
import { useChat } from './ChatProvider'
import { ChatMessageItem } from './ChatMessageItem'
import type { ChatMessage } from './types'
import { formatDateSeparator, isSameGroup } from './utils'
import styles from './Chat.module.css'

interface Props {
  onReply: (m: ChatMessage) => void
  onEdit: (m: ChatMessage) => void
  onDelete: (m: ChatMessage) => void
}

type ListItem =
  | { type: 'sep'; key: string; label: string }
  | {
      type: 'msg'
      key: string
      message: ChatMessage
      showAvatar: boolean
      showAuthor: boolean
      groupPos: 'solo' | 'first' | 'mid' | 'last'
    }

export function ChatMessageList({ onReply, onEdit, onDelete }: Props) {
  const { user } = useAuth()
  const { messages, loading, loadingMore, hasMore, loadMore, open } = useChat()
  const bottomRef = useRef<HTMLDivElement>(null)
  const scrollerRef = useRef<HTMLDivElement>(null)
  const stickBottom = useRef(true)
  const prevLen = useRef(0)

  const items = useMemo(() => {
    const out: ListItem[] = []
    let lastSep = ''
    for (let i = 0; i < messages.length; i++) {
      const m = messages[i]
      const prev = messages[i - 1]
      const next = messages[i + 1]
      const sep = formatDateSeparator(m.created_at)
      if (sep && sep !== lastSep) {
        out.push({ type: 'sep', key: `sep-${sep}-${m.id}`, label: sep })
        lastSep = sep
      }

      const withPrev = isSameGroup(prev, m) && formatDateSeparator(prev.created_at) === sep
      const withNext = isSameGroup(m, next) && formatDateSeparator(next.created_at) === sep
      const mine = m.user_id === user?.id
      let groupPos: 'solo' | 'first' | 'mid' | 'last' = 'solo'
      if (withPrev && withNext) groupPos = 'mid'
      else if (withPrev) groupPos = 'last'
      else if (withNext) groupPos = 'first'

      out.push({
        type: 'msg',
        key: `msg-${m.id}`,
        message: m,
        showAvatar: !mine && (!withNext || Boolean(m.deleted_at)),
        showAuthor: !mine && !withPrev,
        groupPos,
      })
    }
    return out
  }, [messages, user?.id])

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    const onScroll = () => {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight
      stickBottom.current = dist < 80
      if (el.scrollTop < 40 && hasMore && !loadingMore) {
        const prevHeight = el.scrollHeight
        void loadMore().then(() => {
          requestAnimationFrame(() => {
            if (scrollerRef.current) {
              scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight - prevHeight
            }
          })
        })
      }
    }
    el.addEventListener('scroll', onScroll)
    return () => el.removeEventListener('scroll', onScroll)
  }, [hasMore, loadMore, loadingMore])

  useEffect(() => {
    if (!open) return
    const grew = messages.length > prevLen.current
    prevLen.current = messages.length
    if (stickBottom.current || grew) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages, open])

  const jumpReply = (id: number) => {
    const el = document.getElementById(`chat-msg-${id}`)
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const bubble = el?.querySelector(`.${styles.bubble}`)
    if (bubble) {
      bubble.classList.add(styles.bubbleActive)
      window.setTimeout(() => bubble.classList.remove(styles.bubbleActive), 900)
    }
  }

  return (
    <div className={styles.messages} ref={scrollerRef}>
      {hasMore ? (
        <Button size="small" className={styles.loadMore} loading={loadingMore} onClick={() => void loadMore()}>
          Ранее
        </Button>
      ) : null}

      {loading && !messages.length ? (
        <div className={styles.empty}>
          <Spin />
        </div>
      ) : null}
      {!loading && !messages.length ? (
        <div className={styles.empty}>
          <Empty description="Пока нет сообщений" />
        </div>
      ) : null}

      {items.map((item) =>
        item.type === 'sep' ? (
          <div key={item.key} className={styles.dateSep}>
            {item.label}
          </div>
        ) : (
          <ChatMessageItem
            key={item.key}
            message={item.message}
            mine={item.message.user_id === user?.id}
            showAvatar={item.showAvatar}
            showAuthor={item.showAuthor}
            groupPos={item.groupPos}
            onReply={onReply}
            onEdit={onEdit}
            onDelete={onDelete}
            onJumpReply={jumpReply}
          />
        ),
      )}
      <div ref={bottomRef} />
    </div>
  )
}
